<template>
  <v-container fluid class='ma-0 pa-0'>
    <v-dialog
        v-model="isReportDialogOpen"
        max-width="760"
        :fullscreen="xs"
        scrollable
        @click:outside="closeReportDialog"
        >
        <MaintenanceReportList
            :producer_id='selectedProd?.id'
            :producer_name='selectedProd?.display_name'
            :reportType = 'reportType'
            @close="closeReportDialog"
            />
    </v-dialog>
    <v-dialog
        v-model="isRegulatoryDialogOpen"
        max-width="760"
        :fullscreen="xs"
        scrollable
        @click:outside="closeRegulatoryDialog"
        >
        <MaintenanceRegulatoryList
            v-bind:entity_id='selectedProd?.power_station_id ?? 0'
            v-bind:entity_name='selectedProd?.power_station_name ?? ""'
            v-bind:page_title= "'preventive_maintenances.edition.label'"
            v-bind:module_name= "'preventive_maintenances'"
            @close="closeRegulatoryDialog"
            ></MaintenanceRegulatoryList>
    </v-dialog>
    <!-- fixed height so the task list scrolls between its header and its button bar -->
    <v-dialog
        v-model='isVerifMachineOpen'
        :fullscreen="xs"
        :height="xs ? undefined : '92vh'"
        max-width="1400"
        @click:outside="closeVerifDialog"
        >
        <v-card class="vm-card">
            <VerifMachine
                :producer_id=selectedProd?.id
                :producer_name=selectedProd?.display_name
                :dialogMode=dialogMode
                @close="closeVerifDialog"
                ></VerifMachine>
        </v-card>
    </v-dialog>
    <v-table
        density ='compact'
        fixed-header
        height='80vh'
        class="tight-table my-1 pa-0">
      <template v-slot:default>
        <thead>
          <tr>
            <th
                v-for='item in headerItems'
                :key='item.text'
                @click='sortFilteredItems(item)'
                :class='item.class'
                :colspan='item.colspan'
                :rowspan='item.rowspan'
                >
                <v-icon
                    v-show="item.prepend_icon"
                    :icon="item.prepend_icon"
                    />
                  {{ $filters.capitalize($t(item.text))}}
            </th>
          </tr>
          <tr>
            <th
                v-for='item in annualItems'
                :key='item.text'
                @click='sortFilteredItems(item)'
                :class='item.class'
                >
                <v-icon
                    v-show="item.prepend_icon"
                    :icon="item.prepend_icon"
                    />
                  {{ $filters.capitalize($t(item.text)) }}
            </th>
          </tr>
        </thead>

        <tbody>
          <template v-for="station in stations" :key="station.name">
          <!-- station (PDL) line, like the wind production view -->
          <tr class="station-row" @click="toggleStation(station.name)">
            <td colspan="8">
              <div class="station-head">
                <v-icon size="small">{{ isCollapsed(station.name) ? 'mdi-chevron-right' : 'mdi-chevron-down' }}</v-icon>
                <v-icon size="small" color="primary" class="mr-1">mdi-factory</v-icon>
                <span class="station-name">{{ $filters.capitalize(station.name) }}</span>
                <v-chip size="x-small" variant="tonal" class="ml-2">{{ station.items.length }}</v-chip>
                <span class="station-stat">
                  <b>{{ station.done }}</b> / {{ station.total }}
                  <v-progress-linear
                      class="station-bar"
                      :model-value="station.total ? 100 * station.done / station.total : 0"
                      :color="station.overdue ? 'red-darken-1' : station.total && station.done >= station.total ? 'green-darken-1' : 'blue-darken-2'"
                      bg-color="white"
                      height="8"
                      rounded
                      />
                </span>
              </div>
            </td>
          </tr>
          <tr
              v-for="(prod, index) in station.items"
              v-show="!isCollapsed(station.name)"
              :key="prod.id"
              class="producer-row"
              >
              <td class="sticky-column producer-cell">
                {{ $filters.capitalize(prod.display_name) }}
                <span class="producer-type">{{ prod.producer_type__name }}</span>
              </td>
            <td :class="itemClass">
              <ReusableDataDialog
                  :value="prod.annual"
                  @click='openReportDialog(prod,"ac")'
                  >
              </ReusableDataDialog>
            </td>
            <td :class="itemClass">
              <button type="button" class="prog" :class="'prog--' + progressState(prod.ac_done, prod.ac_total, prod.ac_overdue)" :title="prod.ac_overdue ? $t('verif_machines.n_overdue', { n: prod.ac_overdue }) : null" @click='handleVerifMachine(prod, "")'>
                <span class="prog__fill" :style="{ width: progressPct(prod.ac_done, prod.ac_total) + '%' }"></span>
                <span class="prog__label">{{ prod.ac_done }} / {{ prod.ac_total }}</span>
              </button>
            </td>
            <td :class="itemClass">
              <button type="button" class="prog" :class="'prog--' + progressState(prod.ac_done_gr, prod.ac_total_gr, prod.ac_overdue_gr)" :title="prod.ac_overdue_gr ? $t('verif_machines.n_overdue', { n: prod.ac_overdue_gr }) : null" @click='handleVerifMachine(prod, "gr")'>
                <span class="prog__fill" :style="{ width: progressPct(prod.ac_done_gr, prod.ac_total_gr) + '%' }"></span>
                <span class="prog__label">{{ prod.ac_done_gr }} / {{ prod.ac_total_gr }}</span>
              </button>
            </td>
            <td :class="itemClass">
              <button type="button" class="prog" :class="'prog--' + progressState(prod.ac_done_tr, prod.ac_total_tr, prod.ac_overdue_tr)" :title="prod.ac_overdue_tr ? $t('verif_machines.n_overdue', { n: prod.ac_overdue_tr }) : null" @click='handleVerifMachine(prod, "tr")'>
                <span class="prog__fill" :style="{ width: progressPct(prod.ac_done_tr, prod.ac_total_tr) + '%' }"></span>
                <span class="prog__label">{{ prod.ac_done_tr }} / {{ prod.ac_total_tr }}</span>
              </button>
            </td>
            <td :class="itemClass">
              <ReusableDataDialog
                  :value="prod.blades"
                  @click='openReportDialog(prod, "bl")'
                  :limit_red="6"
                  :limit_orange="2"
                  >
              </ReusableDataDialog>
            </td>
            <td :class="itemClass">
              <ReusableDataDialog
                  :value="prod.blade_repairs"
                  :limit_red="100"
                  :limit_orange="100"
                  @click='openReportDialog(prod, "br")'
                  >
              </ReusableDataDialog>
            </td>
            <td :class="itemClass">
              <ReusableDataDialog
                  :value="prod.regulatory"
                  @click='openRegulatoryDialog(prod)'
                  >
              </ReusableDataDialog>
            </td>
          </tr>
          </template>
        </tbody>
      </template>
    </v-table>
  </v-container>
</template>

<script>
  import { useDisplay } from "vuetify";
  import { isNone } from "@/functions.js";
  import ReusableDataDialog from "@/components/maintenance/ReusableDataDialog";
  import VerifMachine from "@/components/maintenance/VerifMachine";
  import MaintenanceReportList from "@/components/maintenance/MaintenanceReportList";
  import MaintenanceRegulatoryList from "@/components/maintenance/MaintenanceRegulatoryList";

  export default {
    components: {
      MaintenanceReportList,
      MaintenanceRegulatoryList,
      VerifMachine,
      ReusableDataDialog,
    },
    mounted(){
      this.loadSummary();
    },
    props: {
      regionFilter: {},
      powerStationFilter:{},
      producerTypeFilter:{},
      searchFilter: {},
    },
    computed: {
      editedItem: function () {
        return this.$store.getters.dataEdit(this.moduleName);
      },
      items: function() {
        return this.$store.state.verif_reports.summary;
      },
      filteredItems: function() {
        let ret_ = this.items
        if (!isNone(this.regionFilter)) ret_ = _.filter(ret_, ['region', this.regionFilter]);
        if (!isNone(this.powerStationFilter)) ret_ = _.filter(ret_, ['power_station_id', this.powerStationFilter]);
        if (!isNone(this.producerTypeFilter)) ret_ = _.filter(ret_, ['producer_type_id', this.producerTypeFilter]);
        if (this.searchFilter) {
          const needle = this.searchFilter.toLowerCase()
          ret_ = ret_.filter(p => (p.display_name || '').toLowerCase().includes(needle)
            || (p.power_station_name || '').toLowerCase().includes(needle))
        }
        ret_ = _.orderBy(ret_, [this.sortBy], [this.order])
        return ret_
      },
      xs() {
        return useDisplay().xs.value
      },
      // producers grouped by power station, stations by name, producers in the chosen sort order
      stations() {
        const groups = new Map()
        for (const prod of this.filteredItems) {
          const name = prod.power_station_name || '-'
          if (!groups.has(name)) groups.set(name, { name, items: [], done: 0, total: 0, overdue: 0 })
          const g = groups.get(name)
          g.items.push(prod)
          g.done += prod.ac_done || 0
          g.total += prod.ac_total || 0
          g.overdue += prod.ac_overdue || 0
        }
        return [...groups.values()].sort((a, b) => a.name.localeCompare(b.name))
      },
    },
    methods: {
      loadSummary() {
        this.$store.dispatch("verif_reports/getSummary")
      },
      sortFilteredItems(item){
        const sortOrder = item.sortOrder
        if (this.sortBy != sortOrder) {
          this.sortBy = sortOrder
          this.order = 'desc';
          this.clearTitleIcons();
          item.prepend_icon = 'mdi-triangle-small-down';
        }
        else if (this.order == 'desc') {
          this.order = 'asc';
          item.prepend_icon = 'mdi-triangle-small-up'
        } else {
          this.order = 'desc';
          item.prepend_icon = 'mdi-triangle-small-down'
        }
      },
      clearTitleIcons() {
        for (let item of this.headerItems) {
          item.prepend_icon = null;
        }
        for (let item of this.annualItems) {
          item.prepend_icon = null;
        }
      },
      progressPct(done, total) {
        return total ? Math.round(100 * done / total) : 0
      },
      // late: a recurring task past its period; done: every task of the cycle finished; none: nothing started; else in progress
      progressState(done, total, overdue) {
        if (!total) return 'empty'
        if (overdue > 0) return 'late'
        if (done >= total) return 'done'
        return done > 0 ? 'partial' : 'none'
      },
      toggleStation(name) {
        const i = this.collapsedStations.indexOf(name)
        if (i < 0) this.collapsedStations.push(name)
        else this.collapsedStations.splice(i, 1)
      },
      isCollapsed(name) {
        return this.collapsedStations.includes(name)
      },
      handleVerifMachine(item, dialogMode){
        this.selectedProd = item
        this.dialogMode = dialogMode
        this.$store.dispatch('verif_machines/getItemsPerProducer',
          {
            producer_id: item.id,
            verif_category: dialogMode
          }
        )
        this.isVerifMachineOpen = true;
      },
      closeVerifDialog(){
        this.loadSummary();
        this.isVerifMachineOpen = false;
      },
      openReportDialog(prod, reportType){
        this.selectedProd = prod;
        this.reportType = reportType
        this.isReportDialogOpen = true;
      },
      closeReportDialog(){
        this.loadSummary();
        this.selectedProd = null;
        this.isReportDialogOpen = false;
      },
      openRegulatoryDialog(prod){
        this.selectedProd = prod;
        this.isRegulatoryDialogOpen= true;
      },
      closeRegulatoryDialog(){
        this.loadSummary();
        this.selectedProd = null;
        this.isRegulatoryDialogOpen = false;
      },
    },
    data() {
      return {
        moduleName: "verif_reports",
        selectedProd: null,
        reportType: 'ac',
        isVerifMachineOpen: false,
        collapsedStations: [],
        isRegulatoryDialogOpen: false,
        isReportDialogOpen: false,
        selectedProd: null,
        dialogMode: undefined,
        sortBy: 'producer',
        order: 'desc',
        power_station_id: undefined,
        itemClass: "pa-0 ma-0 text-center",
        headerItems: [
          {
            "text": 'producers.label',
            "sortOrder": "display_name",
            "prepend_icon": null,
            "colspan": 1,
            "rowspan": 2,
            "class": "text-left sticky-column pl-1 pa-0 ma-0 bg-grey-lighten-4 sticky-thead",
          },
          {
            "text": 'maintenances.annual_closure',
            "sortOrder": "annual",
            "prepend_icon": null,
            "colspan": 1,
            "rowspan": 2,
            "class": "text-center  pa-0 ma-0 sticky-thead",
          },
          {
            "text": 'maintenances.annual_current',
            "prepend_icon": null,
            "colspan": 3,
            "rowspan": 1,
            "class": "text-center  pa-0 ma-0 sticky-thead",
          },
          {
            "text": 'verif_reports.blades',
            "prepend_icon": null,
            "colspan": 2,
            "rowspan": 1,
            "class": "text-center  pa-0 ma-0 sticky-thead",
          },
          {
            "text": 'maintenances.type.regulatory',
            "sortOrder": "regulatory",
            "prepend_icon": null,
            "colspan": 1,
            "rowspan": 2,
            "class": "text-center  pa-0 ma-0 sticky-thead",
          },
        ],
        annualItems: [
          {
            "text": 'generic.all',
            "sortOrder": "ac_done",
            "prepend_icon": null,
            "class": "text-center  pa-0 ma-0 sticky-thead",
          },
          {
            "text": 'maintenances.type.grease',
            "sortOrder": "ac_done_gr",
            "prepend_icon": null,
            "class": "text-center  pa-0 ma-0 sticky-thead",
          },
          {
            "text": 'maintenances.type.transformer',
            "sortOrder": "ac_done_tr",
            "prepend_icon": null,
            "class": "text-center  pa-0 ma-0 sticky-thead",
          },
          {
            "text": 'verif_reports.blade_checks',
            "sortOrder": "blades",
            "prepend_icon": null,
            "class": "text-center  pa-0 ma-0 sticky-thead",
          },
          {
            "text": 'verif_reports.blade_repairs',
            "sortOrder": "blade_repairs",
            "prepend_icon": null,
            "class": "text-center  pa-0 ma-0 sticky-thead",
          },
        ],

      };},
  }
</script>


<style scoped>

table {
  table-layout: auto;
  border-spacing: 0;
  width: 100%;
}

.sticky-column {
  position: -webkit-sticky; /* For Safari */
  position: sticky;
  width: 10em;
  left: 0;
  background: white; /* Ensure background matches table */
  z-index: 1; /* Ensure it is above other cells */
}

.tight-table {
  width: 100%;
  border-collapse: collapse;
  margin: 0;
  padding: 0;
}

.tight-table th,
.tight-table td {
  margin: 0;
  padding: 2px 4px; /* Adjust padding as needed */
}
.tight-table :deep(th) {
  font-size: 0.7rem !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.6) !important;
  background: #fafafa !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1) !important;
  cursor: pointer;
}

.producer-row td {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.producer-row:hover td {
  background: rgba(25, 118, 210, 0.05);
}
.producer-cell {
  padding: 4px 8px !important;
  font-weight: 500;
}
.producer-row:hover .producer-cell {
  background: #eef4fb;
}

/* progress pills for the annual checks: the pill itself is the bar */
.prog {
  position: relative;
  display: inline-block;
  min-width: 92px;
  height: 30px;
  margin: 3px 0;
  padding: 0;
  border: 0;
  border-radius: 15px;
  background: #e3f2fd;
  overflow: hidden;
  font: inherit;
  cursor: pointer;
  box-shadow: inset 0 0 0 1px rgba(25, 118, 210, 0.25);
}
.prog:hover {
  box-shadow: inset 0 0 0 2px #1976d2;
}
.prog__fill {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: #42a5f5;
  transition: width 0.3s;
}
.prog__label {
  position: relative;
  display: block;
  line-height: 30px;
  padding: 0 12px;
  font-weight: 800;
  font-size: 0.9rem;
  font-variant-numeric: tabular-nums;
  color: #0d47a1;
  white-space: nowrap;
  text-align: center;
}
.prog--done {
  background: #43a047;
  box-shadow: inset 0 0 0 1px #2e7d32;
}
.prog--done .prog__fill {
  background: #43a047;
}
.prog--done .prog__label {
  color: #fff;
}
.prog--done:hover {
  box-shadow: inset 0 0 0 2px #1b5e20;
}
.prog--late {
  background: #ffebee;
  box-shadow: inset 0 0 0 1px #d32f2f;
}
.prog--late .prog__fill {
  background: #ef9a9a;
}
.prog--late .prog__label {
  color: #b71c1c;
}
.prog--none {
  background: #fff3e0;
  box-shadow: inset 0 0 0 1px #ffb74d;
}
.prog--none .prog__label {
  color: #e65100;
}
.prog--empty {
  background: #eeeeee;
  box-shadow: none;
  cursor: default;
}
.prog--empty .prog__label {
  color: rgba(0, 0, 0, 0.4);
}

.vm-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.producer-type {
  display: block;
  font-size: 0.7rem;
  line-height: 1.1;
  color: rgba(0, 0, 0, 0.55);
}

/* station (PDL) lines */
.tight-table :deep(tr.station-row td) {
  background: #bbdefb;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding: 4px 8px !important;
  cursor: pointer;
}
.tight-table :deep(tr.station-row:hover td) {
  background: #90caf9;
}
.station-head {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.station-name {
  font-weight: 700;
  font-size: 0.95rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.station-stat {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 18px;
  font-size: 0.85rem;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.station-bar {
  width: 90px;
}

</style>
